
import {
  defineComponent,
  onUpdated,
  onMounted,
  computed,
  reactive,
  toRefs,
  ref,
  watch
} from 'vue'
import CountTo from '@/components/CountTo.vue'
import Counter from '@/components/Counter.vue'
import { useStore } from 'vuex'

import Directr from '@/services/directr'
import PageFilter from '@/layouts/PageFilter.vue'
import useFullscreen from '@/composables/useFullscreen'
import useIO from '../../composables/useIO'

import { InsightPoint } from '@/models/interfaces/insight'
import { format } from '@/utils/template'
import moment from 'moment'
import AreaChart from '@/components/Chart/AreaChart.vue'
import { DEFAULT_AXES } from '../Insight/constants'
import { useQuasar } from 'quasar'

import { useRoute, useRouter } from 'vue-router'
import ProgressCard from '@/components/ProgressCard.vue'
import AllClearImg from '@/components/AllClearImg.vue'

import SvgIcon from '@/components/SvgIcon.vue'
import DButton from '@/components/base/DButton.vue'
import useDeviceSettings from '@/composables/useDeviceSettings'
import { CtTask } from '@/models/interfaces/content-type'
import usePageUtil from '@/pages/composables/usePageUtil'
import { debounce } from '@/utils'
import { User, UUID } from '@fusionauth/typescript-client'

// import { date } from 'quasar'

const DATE_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSSZ'
const STALE_MOMENT = moment().subtract(2, 'months')
// TODO: should show role-based daily overview component
// i.e operational overview, picking tasks overview, warehouse overview etc.

interface OperationalDashState {
  orders?: number,
  fulfilled_orders?: number,
  issue_orders: number | null,
  picking_orders: number,
  drafts: number | null,
  source_orders: number,
  picking_tasks: number,
  points?: Array<InsightPoint>
}

export default defineComponent({
  name: 'OperationalDashboard',
  props: {
    toggleable: {
      type: Boolean,
      default: true
    },
    minimized: {
      type: Boolean,
      default: false
    },
    minimizedLabel: {
      type: String,
      default: 'Show Dashboard'
    },
    expandedLabel: {
      type: String,
      default: 'Hide Dashboard'
    }
  },
  components: { DButton, CountTo, PageFilter, Counter, AreaChart, ProgressCard, SvgIcon, AllClearImg },
  emits: ['update:minimized'],
  setup (props, { emit }) {
    const $q = useQuasar()
    const isMobile = computed(() => $q.screen.lt.sm)

    const $store = useStore()
    const $router = useRouter()
    const $route = useRoute()
    const $directr: Directr = $store.getters['Services/Directr']
    const $IO = useIO($store)
    const fullscreen = useFullscreen($store)
    const $settings = useDeviceSettings($store)

    const roles = computed<User>(() => $store.getters['User/Roles'])
    const $userscope = computed(() => $store.getters['User/ScopeData'])
    const $userId = computed<UUID>(() => $store.getters['User/User']?.id)

    const FULFILLMENT_TARGET = computed(() => Math.min(250, (state.orders || 0) + (state.fulfilled_orders || 0) + (state.picking_orders || 0)))

    // const $router = useRouter()
    const state = reactive<OperationalDashState>({
      orders: 0,
      fulfilled_orders: 0,
      issue_orders: null,
      source_orders: 0,
      picking_tasks: 0,
      picking_orders: 0,
      drafts: null,
      points: undefined
    })

    const search = reactive({
      show: false,
      contentType: $store.getters['Products/Type'],
      model: $store.getters['Products/Model'],
      items: [],
      index: -1,
      target: undefined as any
    })

    const loading = ref(false)
    const error = ref(null)

    const isScoped = computed(() => !!$userscope.value?.channels)
    const channelSelection = computed(() => $userscope.value?.channels || [])

    async function load () {
      try {
        error.value = null
        loading.value = true
        const select = { select: ['reference', 'status', 'status_info', 'tags', 'shipping_address', 'catalogue'] }

        // const { items: orders } = await $directr.get('order',
        //   [
        //     select,
        //     { field: 'status', method: 'nin', match: 'resolved' },
        //     { field: 'status', method: 'nin', match: 'final' },
        //     { field: 'status', method: 'nin', match: 'cancelled' },
        //     { field: 'sys.created_at', method: 'gte', match: STALE_MOMENT.endOf('day').format(DATE_FORMAT) }
        //   ])
        const { items: orders } = await $directr.get('orders.pending.count', channelSelection.value || [], { force: $settings.settings.value.showNewAsPackable })
        state.orders = parseInt(orders?.[0]?.count) || 0

        const fulfilledQuery = [
          { select: ['id', 'status'] },
          { field: 'status', method: 'lin', match: ['resolved', 'transiting', 'final'] },
          { field: 'sys.updated_at', method: 'gte', match: `${moment().startOf('day').format(DATE_FORMAT)}` }
        ]

        if (channelSelection.value?.length)
          fulfilledQuery.push({ field: 'catalogue', method: 'lin', match: channelSelection.value })

        const { items: fulfilled } = await $directr.get('order', fulfilledQuery)
        state.fulfilled_orders = (fulfilled?.filter(o => o.status?.length && !o.status?.includes('cancelled')) || []).length || 0

        const { items: issue_orders } = await $directr.get('issues.count', $userscope.value?.channels || [])
        state.issue_orders = (parseInt(issue_orders?.[0].issues) || 0)


        const { tasks } = await $store.dispatch('Tasks/Fetch', { type: 'pick', pending: true, created_by: isScoped.value ? $userId.value : undefined })
        const pickTasks: Array<CtTask> = Object.values(tasks?.pick || {} as any)
        state.picking_orders = pickTasks.reduce((orders, task) => {
          return orders.concat(Object.values(task.meta || {}).flatMap((m: any) => m.orders))
        }, [] as Array<string>).filter((f,i,a) => Boolean(f) && a.indexOf(f) === i).length
        state.picking_tasks = pickTasks.length

        // load open source_orders
        const { items: source_orders } = await $directr.get('source_order', [{ select: ['reference', 'delivery', 'status', 'shipping_address', 'catalogue'] }, { field: 'status', method: 'nin', match: 'resolved' }, { field: 'status', method: 'nin', match: 'final' }, { field: 'status', method: 'nin', match: 'cancelled' }])
        state.source_orders = (source_orders?.filter(o => o.delivery?.date && moment(o.delivery?.date).isBetween(moment().startOf('day'), moment().endOf('day'))) || []).length

        const drafts = isScoped.value ? ({ items:[] }) : await $directr.get('draft', [{ select: ['id'] },{ field: 'type', method: 'is', match: 'orders' }, { field: 'status', method: 'nin', match: 'paused' }])
        state.drafts = drafts.items?.length || 0

        // const points = await $directr.insight.get('order', { start: moment().startOf('week').toDate(), end: moment().endOf('week').toDate() }, InsightResolution.Day)
        // const points = await $directr.insight.get('order', { start: moment().subtract(1, 'week').toDate(), end: moment().toDate() }, InsightResolution.Day)
        // state.points = points?.items
      } catch (e: any) {
        console.error(e)
        error.value = e
      } finally {
        loading.value = false
      }
    }

    async function rerouteByRole (roles: Array<string> = $store.getters['User/Roles']) {
      const role = roles[0]
      switch (role) {
        case 'picker':
        case 'packer':
          return $router.replace({ name: 'DashboardOrders' })
        default:
        case 'admin':
          return load()
      }
    }

    // onUpdated(() => { if (isMobile.value) void $store.commit('App/HideMobileNav', true) })
    onMounted(async () => {
      await rerouteByRole()
      // if (isMobile.value) void $store.commit('App/HideMobileNav', true)
    })

    const debouncedLoad = debounce(load, 3000)
    $IO.listen('update/order', ({ message, payload }) => {
      debouncedLoad()
    })
    // $IO.listen('create/order', ({ message, payload }) => {
    //   if (process.env.NODE_ENV !== 'production' || (window.origin || '').includes('staging')) console.log('Socket/create | ', message, payload)
    //   const pushable = payload.items || (payload?.sys && isUUID(payload.sys?.id) ? payload : undefined)
    //   if (pushable && state.orders) {
    //     const i = state.orders?.findIndex(o => o.sys.id === pushable.sys?.id)
    //     if (i < 0) state.orders.push(pushable)
    //   }
    // })

    const ordersByDay = computed(() => {
      return (state.points || [])
        .map(point => ({ group: 'Orders', key: format.date(point.timestamp, DATE_FORMAT), value: point.total }))
    })

    const productsByDay = computed(() => {
      return (state.points || [])
        .map(point => ({ group: 'Products', key: format.date(point.timestamp, DATE_FORMAT), value: point.points.products || 0 }))
    })


    const internalMizimized = ref($settings.settings.value?.hideOperationalDashboard !== undefined ? $settings.settings.value?.hideOperationalDashboard : props.minimized)
    watch(() => props.minimized, value => { internalMizimized.value = value })
    function toggleDashboard (value: boolean) {
      if (!props.toggleable) return
      internalMizimized.value = value

      // set cookie
      $settings.set('hideOperationalDashboard', value)

      emit('update:minimized', value)
    }

    const pageUtil = usePageUtil($route, __dirname)
    function toPicking () {
      pageUtil.showView({ name: 'pick', id: 'Order/Pick' })
    }
    function toPacking () {
      pageUtil.showView({ name: 'pack', id: 'Order/Pack' })
    }
    function toSupplyOrders () {
      void $router.push({ name: 'SupplyOrders', params: { view: 'orders' } })
    }

    return {
      state,
      roles,
      settings: $settings.settings,
      internalMizimized,
      toggleDashboard,
      toPicking,
      toPacking,
      toSupplyOrders,
      count: toRefs(state),
      loading,
      error,
      search,
      directr: $directr,
      fullscreen,
      FULFILLMENT_TARGET,
      charts: {
        productsAndOrdersByDay: {
          data: computed(() => ordersByDay.value.concat(productsByDay.value)),
          options: {
            axes: DEFAULT_AXES,
            height: '300px'
          }
        }
      }
    }
  }
})
